import React from "react";
import { Fade, Skeleton, useTheme, CircularProgress, MenuItem, Stack } from "@mui/material";
import {
  PlayerPanel,
  PlayPauseButtonWrapper,
  PlayPauseButton,
  ProgressRing,
  ProgressTrack,
  ProgressCircle,
  ContentContainer,
  TextContainer,
  MainTextWrapper,
  MainText,
  CaptionText,
  CloseButton,
} from "./TTSPlayer.styles";
import useTTSPlayer from "./TTSPlayer.hook";
import CloseIcon from "src/icons/player/CloseIcon";
import InnerPlayIcon from "src/icons/player/InnerPlayIcon";
import InnerPauseIcon from "src/icons/player/InnerPauseIcon";
import DownloadIcon from "src/icons/player/DownloadIcon";
import ThreeDotIcon from "src/icons/player/ThreeDotIcon";
import HyroThreeDotMenu from "@hyro-components/HyroThreeDotMenu";
import Tooltip from "@hyro-components/Tooltip";
import { TOOLTIP_DELAY } from "@utils";

const VOICE_TOOLTIP = "To update the agent's voice, go to Settings.";
const PROGRESS_RADIUS = 24;
const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RADIUS;

const TTSPlayer = ({ instanceId }) => {
  const theme = useTheme();
  const {
    shouldScroll,
    textRef,
    wrapperRef,
    text,
    progress,
    voiceName,
    isPlaying,
    isLoading,
    isOpen,
    duration,
    handleClose,
    handlePlayPause,
    downloadCurrentAudio,
  } = useTTSPlayer({ instanceId });

  return (
    <Fade in={isOpen} timeout={300} unmountOnExit>
      <PlayerPanel data-testid="tts-player-panel">
        <PlayPauseButtonWrapper>
          <ProgressRing viewBox="0 0 52 52">
            <ProgressTrack cx="26" cy="26" r={PROGRESS_RADIUS} />
            <ProgressCircle
              cx="26"
              cy="26"
              r={PROGRESS_RADIUS}
              style={{
                strokeDasharray: PROGRESS_CIRCUMFERENCE,
                strokeDashoffset: PROGRESS_CIRCUMFERENCE * (1 - progress),
                transition: "stroke-dashoffset 0.2s linear",
              }}
            />
          </ProgressRing>
          <PlayPauseButton
            onClick={() => handlePlayPause({ text })}
            aria-label={isPlaying ? "Pause" : "Play"}
            data-testid="tts-player-play-pause-button"
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: theme.palette.system[0] }} /> : (
              <>
                {isPlaying ? (
                  <InnerPauseIcon />
                ) : (
                  <InnerPlayIcon />
                )}
              </>
            )}
          </PlayPauseButton>
        </PlayPauseButtonWrapper>

        <ContentContainer>
          <TextContainer>
            <MainTextWrapper ref={wrapperRef} isLoading={isLoading} shouldScroll={shouldScroll}>
              {isLoading ? <Skeleton variant="text" sx={{ ...theme.typography.body2, width: 268 }} /> : (
                <MainText ref={textRef} shouldScroll={shouldScroll} sx={{ animationDuration: duration }} data-testid="tts-player-text">
                  {text || "No text to speak"}&nbsp;
                  {shouldScroll && <span>{text}&nbsp;</span>}
                </MainText>
              )}
            </MainTextWrapper>
            {isLoading ? <Skeleton variant="text" sx={{ ...theme.typography.caption, width: 123 }} /> : (
              <Tooltip
                title={VOICE_TOOLTIP}
                placement="top"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 270 },
                  },
                }}
              >
                <CaptionText data-testid="tts-player-voice-name">{voiceName}</CaptionText>
              </Tooltip>
            )}
          </TextContainer>

          <Stack sx={{ gap: .5 }} direction='row'>
            {!isLoading && (
              <HyroThreeDotMenu
                icon={<ThreeDotIcon style={{ flexShrink: 0 }} />}
                testId="tts-player-action-menu"
                sx={{ width: 20, height: 20, flexShrink: 0 }}
                menuItems={[
                  <MenuItem
                    key="download-audio"
                    onClick={downloadCurrentAudio}
                    data-testid="tts-player-download-audio"
                    sx={{ gap: 1, borderRadius: 1, ...theme.typography.body2 }}
                  >
                    <DownloadIcon />
                    Download audio
                  </MenuItem>
                ]}
              />
            )}

            <Tooltip enterDelay={TOOLTIP_DELAY.LONG} title="Close player" placement="top">
              <CloseButton onClick={handleClose} aria-label="Close player" data-testid="tts-player-close-button">
                <CloseIcon style={{ flexShrink: 0 }} />
              </CloseButton>
            </Tooltip>
          </Stack>
        </ContentContainer>
      </PlayerPanel>
    </Fade>
  );
};

export default TTSPlayer;
